import { useEffect, useState } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  LineChart, Line, CartesianGrid,
} from "recharts";
import { Activity, Loader2, HeartPulse, Moon, Footprints, Upload } from "lucide-react";
import { motion } from "framer-motion";
import { API, useAuth } from "@/App";

const iconForType = (t) => {
  if (!t) return Activity;
  const x = t.toLowerCase();
  if (x.includes("step") || x.includes("distance")) return Footprints;
  if (x.includes("heart")) return HeartPulse;
  if (x.includes("sleep")) return Moon;
  return Activity;
};

const HealthDataHistory = () => {
  const { token } = useAuth();
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeType, setActiveType] = useState(null);
  const [samples, setSamples] = useState([]);
  const [sampleLoading, setSampleLoading] = useState(false);

  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  useEffect(() => {
    axios.get(`${API}/wellness/health-import/summary`, { headers, withCredentials: true })
      .then(res => {
        setSummary(res.data);
        if (res.data.by_type?.[0]) setActiveType(res.data.by_type[0].type);
      })
      .catch(() => setSummary({ by_type: [], total: 0 }))
      .finally(() => setLoading(false));
    // eslint-disable-next-line
  }, [token]);

  useEffect(() => {
    if (!activeType || !token) return;
    setSampleLoading(true);
    axios.get(`${API}/wellness/health-import?type=${encodeURIComponent(activeType)}&days=30`, { headers, withCredentials: true })
      .then(res => setSamples(res.data.samples || []))
      .catch(() => setSamples([]))
      .finally(() => setSampleLoading(false));
    // eslint-disable-next-line
  }, [activeType, token]);

  const chartData = samples
    .filter(s => typeof s.value === "number" || !isNaN(parseFloat(s.value)))
    .map(s => ({
      date: (s.start || s.end || "").slice(5, 10),
      value: typeof s.value === "number" ? s.value : parseFloat(s.value),
    }))
    .slice(-30);

  return (
    <div className="space-y-6" data-testid="health-history-page">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-center">
        <h1 className="text-3xl md:text-4xl font-bold text-white mb-2">
          Health <span className="gradient-text">Data</span>
        </h1>
        <p className="text-mystic-200/70">Steps, sleep, heart rate and more — synced from your wearable</p>
      </motion.div>

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 animate-spin text-mystic-300" />
        </div>
      ) : !summary?.by_type?.length ? (
        <Card className="glass">
          <CardContent className="py-14 text-center">
            <Upload className="w-10 h-10 text-mystic-400 mx-auto mb-3" />
            <p className="text-white mb-1">No health data yet</p>
            <p className="text-mystic-200/70 text-sm max-w-md mx-auto">
              Open the Sacred Celestial Flow mobile app, grant HealthKit or Google Fit permission, and tap "Sync to Cloud". Your data will appear here.
            </p>
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Data types grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {summary.by_type.map((t) => {
              const Icon = iconForType(t.type);
              const active = activeType === t.type;
              return (
                <button
                  key={`${t.source}-${t.type}`}
                  onClick={() => setActiveType(t.type)}
                  data-testid={`health-type-${(t.type || "x").toLowerCase()}-btn`}
                  className={`text-left rounded-xl p-4 border transition-all ${
                    active
                      ? "border-mystic-300 bg-mystic-500/15"
                      : "border-white/10 bg-mystic-900/40 hover:border-mystic-400/40"
                  }`}
                >
                  <Icon className={`w-5 h-5 mb-2 ${active ? "text-mystic-200" : "text-mystic-400"}`} />
                  <p className="text-white text-sm font-medium capitalize">{(t.type || "").replace(/_/g, " ")}</p>
                  <p className="text-mystic-200/60 text-xs mt-0.5">{t.count} samples · {t.source}</p>
                </button>
              );
            })}
          </div>

          {/* Chart */}
          <Card className="glass">
            <CardHeader>
              <CardTitle className="text-white capitalize">{activeType?.replace(/_/g, " ") || "Trend"}</CardTitle>
            </CardHeader>
            <CardContent>
              {sampleLoading ? (
                <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-mystic-300" /></div>
              ) : chartData.length < 2 ? (
                <p className="text-mystic-200/60 text-sm text-center py-8">Not enough samples to chart yet.</p>
              ) : activeType?.toLowerCase().includes("step") ? (
                <ResponsiveContainer width="100%" height={260}>
                  <BarChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#ffffff12" />
                    <XAxis dataKey="date" stroke="#c4a0ff" fontSize={11} />
                    <YAxis stroke="#c4a0ff" fontSize={11} />
                    <Tooltip contentStyle={{ background: "#1a0d2e", border: "1px solid #3b1f6e", borderRadius: 8, color: "#fff" }} />
                    <Bar dataKey="value" fill="#9d4edd" radius={[6, 6, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <ResponsiveContainer width="100%" height={260}>
                  <LineChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#ffffff12" />
                    <XAxis dataKey="date" stroke="#c4a0ff" fontSize={11} />
                    <YAxis stroke="#c4a0ff" fontSize={11} />
                    <Tooltip contentStyle={{ background: "#1a0d2e", border: "1px solid #3b1f6e", borderRadius: 8, color: "#fff" }} />
                    <Line type="monotone" dataKey="value" stroke="#f472b6" strokeWidth={2} dot={{ r: 3 }} />
                  </LineChart>
                </ResponsiveContainer>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
};

export default HealthDataHistory;
